import React from 'react';
import { fetchSessionOnce } from '../core/session/SessionSingleFlight';
import { fetchCapabilityProjection } from '../core/auth/capabilityProjection';
import type { 
	PluginManifest, 
	LoadedPlugin,
	MenuConfig, 
	MenuStructure, 
	MenuColumn,
	MenuDefaults,
	ResolvedMenuGroup, 
	ResolvedMenuColumn, 
	ResolvedMenuItem 
} from './types';

/**
 * PermittedRoute - A route that the user is permitted to access
 * Magnum UX 3.1.0: Supports route-level navigation contributions
 */
export type PermittedRoute = { 
	pluginId: string; 
	path: string; 
	label: string; 
	group?: string; 
	icon?: string;
	/** Static, manifest-authored one-liner shown under the tile label. Never a live plugin fetch. */
	description?: string;
	/** Sort order within group (lower = first) */
	order?: number;
	/** Whether this is a sub-route contribution (not the plugin's primary entry) */
	isContribution?: boolean;
};

async function fetchManifests(signal?: AbortSignal): Promise<PluginManifest[]> {
	const base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	const url = `${base}/api/plugins/manifests`;
	const res = await fetch(url, { credentials: 'include', signal });
	if (!res.ok) throw new Error(`manifests:${res.status}`);
	const data = await res.json();
	// BFF returns { manifests: [...] }, extract the array
	return data.manifests || data || [];
}

async function fetchFullManifest(pluginId: string, signal?: AbortSignal): Promise<PluginManifest | null> {
	const base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	try {
		const res = await fetch(`${base}/api/plugins/manifests/${encodeURIComponent(pluginId)}`, { 
			credentials: 'include', 
			signal 
		});
		if (!res.ok) {
			if (res.status === 404) return null;
			throw new Error(`Failed to load manifest for ${pluginId}: ${res.status}`);
		}
		return await res.json();
	} catch (error) {
		console.error(`[dynamicNav] Failed to load manifest for ${pluginId}:`, error);
		return null;
	}
}

function humanize(pluginId: string, path: string, explicitTitle?: string | null, icon?: string): string | null {
    // Return null if title is explicitly null (hidden from menu)
    if (explicitTitle === null) return null;
    
    const base = (() => {
        if (explicitTitle) return explicitTitle;
        if (/ai-spend/.test(path) || /ai[-_]spend/.test(pluginId)) return 'AI Spend';
        return pluginId.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    })();
    const title = /\/admin$/i.test(path) ? `${base} Admin` : base;
    
    // Return title only; icon is rendered separately in tiles to avoid double icons
    return title;
}

function dedupe(items: PermittedRoute[]): PermittedRoute[] {
	const seen = new Set<string>();
	const out: PermittedRoute[] = [];
	for (const it of items) {
		const key = it.path;
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(it);
	}
	return out;
}

// =============================================================================
// MEGA MENU RESOLVER
// Per Blue Steel: Config-driven, resilient, never throws
// =============================================================================

/** Default values used when config doesn't specify defaults */
const FALLBACK_DEFAULTS: MenuDefaults = {
	icon: 'app-window',
	group: 'Other',
	description: '',
	primaryRoutePrefix: '/',
};

// =============================================================================
// AUTHORIZED MANIFESTS CACHE
//
// `LayoutShell` calls `fetchAuthorizedPluginManifests()` from multiple hooks
// (mega menu, header widgets, plugin routes). Without a shared cache, we end up
// issuing multiple identical manifest fetches + capability projections at login.
//
// We dedupe in-flight requests and cache results briefly *per subject*.
//
// Critical: never reuse cached authz results across subjects. This avoids a
// subtle but severe bug where a user switch (logout/login, impersonation, etc.)
// could briefly display the previous user's permitted navigation.
//
// We intentionally do not wire the caller's AbortSignal into the shared
// in-flight request because one component unmounting would cancel the request
// for all consumers.
// =============================================================================
const AUTHORIZED_MANIFESTS_TTL_MS = 30_000;
type CachedAuthorizedManifests = { ts: number; manifests: PluginManifest[] };
const authorizedManifestsCacheByAuthContext = new Map<string, CachedAuthorizedManifests>();
const authorizedManifestsInFlightByAuthContext = new Map<string, Promise<PluginManifest[]>>();

function isTestMode(): boolean {
	// Vitest/Vite set this to "test".
	const mode = (import.meta as any).env?.MODE;
	return mode === 'test';
}

function getUserArnFromSessionJson(j: any): string | null {
	// Excellence Playbook identity standard:
	// MUST use `/auth/session` and the full `user_arn` (no short IDs).
	const direct =
		j?.user_arn ??
		j?.user?.user_arn ??
		j?.profile?.user_arn ??
		j?.['user_arn'] ??
		j?.['https://schemas.empowernow.dev/identity/account_arn'] ??
		j?.user?.['https://schemas.empowernow.dev/identity/account_arn'] ??
		null;

	if (typeof direct === 'string' && direct.trim().length > 0) return direct.trim();
	return null;
}

function pruneAuthorizedManifestsCache(now: number): void {
	for (const [k, v] of authorizedManifestsCacheByAuthContext.entries()) {
		if (now - v.ts >= AUTHORIZED_MANIFESTS_TTL_MS) {
			authorizedManifestsCacheByAuthContext.delete(k);
		}
	}

	// Bound cache size defensively (CSRF may rotate; this prevents unbounded growth).
	if (authorizedManifestsCacheByAuthContext.size > 5) {
		const entries = Array.from(authorizedManifestsCacheByAuthContext.entries()).sort((a, b) => a[1].ts - b[1].ts);
		for (let i = 0; i < entries.length - 5; i++) {
			authorizedManifestsCacheByAuthContext.delete(entries[i][0]);
		}
	}
}

/**
 * Translate a menu label using shell catalog
 * Falls back to literal value if no key or translation not found
 * 
 * @param key Optional i18n key (e.g., 'shell.menu.groups.administration')
 * @param fallback Literal string to use if key not found
 * @returns Translated string or fallback
 */
function translateMenuLabel(key: string | undefined, fallback: string): string {
	if (!key) return fallback;
	
	// Access global i18n state from LocalizationProvider
	const globalI18n = (window as any).__EXPERIENCE_I18N__;
	if (!globalI18n?.shellCatalog) return fallback;
	
	const translated = globalI18n.shellCatalog[key];
	return translated || fallback;
}

/**
 * Fetch menu configuration from BFF
 * Returns null if no menu_structure is configured (use legacy grouping)
 */
export async function fetchMenuConfig(signal?: AbortSignal): Promise<MenuConfig | null> {
	const base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	try {
		const res = await fetch(`${base}/api/plugins/menu-config`, { credentials: 'include', signal });
		if (!res.ok) {
			if (res.status === 404) return null; // Not configured, use legacy
			throw new Error(`menu-config:${res.status}`);
		}
		return await res.json();
	} catch (error) {
		console.warn('[MegaMenu] Failed to fetch menu config, using legacy grouping:', error);
		return null;
	}
}

/**
 * Resolve manifests the current user is authorized to see in navigation.
 * SECURITY: fail-closed - only explicit allow decisions are included.
 */
export async function fetchAuthorizedPluginManifests(signal?: AbortSignal): Promise<PluginManifest[]> {
	// Respect caller abort early (keeps previous semantics for consumers that
	// rely on AbortController), but don't share the signal with the global fetch.
	if (signal?.aborted) {
		throw new DOMException('Aborted', 'AbortError');
	}

	// Excellence Playbook: Identity Handling (CRITICAL)
	// Always key any auth-derived caching by the full `user_arn` from `/auth/session`.
	// This avoids cross-user bleed during impersonation / fast re-login flows.
	const session = await fetchSessionOnce();
	if (!(session as any)?.authenticated) return [];
	const userArn = getUserArnFromSessionJson(session);
	if (!userArn) return [];

	const key = `user_arn:${userArn}`;
	const now = Date.now();

	// In tests we avoid caching to prevent cross-test interference.
	if (!isTestMode()) {
		if (key) {
			pruneAuthorizedManifestsCache(now);
			const cached = authorizedManifestsCacheByAuthContext.get(key);
			if (cached && now - cached.ts < AUTHORIZED_MANIFESTS_TTL_MS) return cached.manifests;
			const inFlight = authorizedManifestsInFlightByAuthContext.get(key);
			if (inFlight) return inFlight;
		}
	}

	const load = (async (): Promise<PluginManifest[]> => {
		// Important: no external AbortSignal here (see header comment).
		const manifests = await fetchManifests();
		const enabledManifests = manifests.filter(manifest => manifest.enabled !== false);

		if (enabledManifests.length === 0) {
			return [];
		}

		// Canonical path: ONE capability-document fetch (globally de-duped and
		// cached per subject), not one PDP probe per plugin.
		const projection = await fetchCapabilityProjection(enabledManifests);
		const visible = new Set(projection.visible_plugins);
		const authorized = enabledManifests.filter(manifest => visible.has(manifest.id));
		if (import.meta.env.DEV) {
			console.debug('[dynamicNav] Authorized plugins via capability projection', {
				authorized: authorized.length,
				totalEnabled: enabledManifests.length,
				degraded: projection.degraded,
			});
		}
		return authorized;
	})();

	if (!isTestMode()) {
		if (key) {
			authorizedManifestsInFlightByAuthContext.set(key, load);
		}
	}

	try {
		const manifests = await load;
		if (!isTestMode()) {
			if (key) {
				authorizedManifestsCacheByAuthContext.set(key, { ts: Date.now(), manifests });
			}
		}
		return manifests;
	} finally {
		if (!isTestMode()) {
			if (key) {
				authorizedManifestsInFlightByAuthContext.delete(key);
			}
		}
	}
}

/**
 * Resolve a single menu item from plugin ID
 * Uses pre-computed auth results from batch check
 * Returns null if plugin doesn't exist or user can't access
 */
function resolveMenuItem(
	pluginId: string,
	pluginMap: Map<string, PluginManifest>,
	authResults: Map<string, boolean | 'unknown'>,
	defaults: MenuDefaults,
	currentPath: string
): ResolvedMenuItem | null {
	const plugin = pluginMap.get(pluginId);
	
	if (!plugin) {
		console.debug(`[MegaMenu] Plugin "${pluginId}" in menu_structure but not in authorized set`);
		return null;
	}
	
	if (plugin.enabled === false) {
		console.debug(`[MegaMenu] Plugin "${pluginId}" is disabled, skipping`);
		return null;
	}
	
	// SECURITY: Fail-closed authorization
	// Only show plugins with explicit "true" authorization
	// false = denied, 'unknown' = error/timeout, undefined = not checked
	const accessible = authResults.get(pluginId);
	
	if (accessible !== true) {
		// Fail-closed: hide unless explicitly authorized
		const reason = accessible === false ? 'denied' 
			: accessible === 'unknown' ? 'auth error/timeout'
			: 'not in auth results';
		console.debug(`[MegaMenu] Hiding plugin "${pluginId}" (${reason})`);
		return null;
	}
	
	const path = plugin.primary_route ?? `/${plugin.id}`;
	const isCurrent = currentPath === path || currentPath.startsWith(path + '/');
	
	// At this point, accessible === true (fail-closed check passed)
	return {
		id: plugin.id,
		label: plugin.title ?? plugin.name ?? plugin.id,
		path: path,
		icon: plugin.icon ?? defaults.icon,
		description: plugin.description ?? defaults.description,
		accessible: true,
		isCurrent,
	};
}

/**
 * Resolve a menu column with all its items
 * Uses pre-computed auth results (synchronous)
 */
function resolveColumn(
	column: MenuColumn,
	pluginMap: Map<string, PluginManifest>,
	authResults: Map<string, boolean | 'unknown'>,
	defaults: MenuDefaults,
	currentPath: string
): ResolvedMenuColumn | null {
	const items: ResolvedMenuItem[] = [];
	
	for (const pluginId of column.items) {
		const resolved = resolveMenuItem(pluginId, pluginMap, authResults, defaults, currentPath);
		if (resolved) {
			items.push(resolved);
		}
	}
	
	// Skip empty columns
	if (items.length === 0) {
		return null;
	}
	
	return {
		// Use titleKey for i18n, fallback to title
		title: translateMenuLabel(column.titleKey, column.title),
		items,
	};
}

/**
 * Resolve a complete menu group
 * Uses pre-computed auth results (synchronous after batch auth check)
 */
function resolveGroup(
	groupConfig: MenuStructure,
	pluginMap: Map<string, PluginManifest>,
	authResults: Map<string, boolean | 'unknown'>,
	defaults: MenuDefaults,
	currentPath: string
): ResolvedMenuGroup | null {
	// Group with columns -> mega menu
	if (groupConfig.columns && groupConfig.columns.length > 0) {
		const columns: ResolvedMenuColumn[] = [];
		
		for (const colConfig of groupConfig.columns) {
			const resolved = resolveColumn(colConfig, pluginMap, authResults, defaults, currentPath);
			if (resolved) {
				columns.push(resolved);
			}
		}
		
		if (columns.length === 0) {
			return null;
		}
		
		return {
			// Use groupKey for i18n, fallback to group
			group: translateMenuLabel(groupConfig.groupKey, groupConfig.group),
			icon: groupConfig.icon,
			type: 'mega',
			columns,
		};
	}
	
	// Group with items -> simple dropdown
	if (groupConfig.items && groupConfig.items.length > 0) {
		const items: ResolvedMenuItem[] = [];
		
		for (const pluginId of groupConfig.items) {
			const resolved = resolveMenuItem(pluginId, pluginMap, authResults, defaults, currentPath);
			if (resolved) {
				items.push(resolved);
			}
		}
		
		if (items.length === 0) {
			return null;
		}
		
		return {
			// Use groupKey for i18n, fallback to group
			group: translateMenuLabel(groupConfig.groupKey, groupConfig.group),
			icon: groupConfig.icon,
			type: 'dropdown',
			items,
		};
	}
	
	return null;
}

/**
 * Build legacy grouping when menu_structure is missing
 * Groups plugins by their `group` field - compatible with existing config
 * Uses pre-computed auth results to filter inaccessible plugins
 */
function buildLegacyGrouping(
	plugins: PluginManifest[],
	authResults: Map<string, boolean | 'unknown'>,
	defaults: MenuDefaults,
	currentPath: string
): ResolvedMenuGroup[] {
	const grouped = new Map<string, PluginManifest[]>();
	
	for (const plugin of plugins) {
		if (plugin.enabled === false) continue;
		
		// SECURITY: Fail-closed - skip unless explicitly authorized
		const accessible = authResults.get(plugin.id);
		if (accessible !== true) continue;
		
		const groupName = plugin.group ?? defaults.group;
		if (!grouped.has(groupName)) {
			grouped.set(groupName, []);
		}
		grouped.get(groupName)!.push(plugin);
	}
	
	return Array.from(grouped.entries())
		.map(([groupName, groupPlugins]): ResolvedMenuGroup => {
			return {
				group: groupName,
				icon: groupPlugins[0]?.icon ?? defaults.icon,
				type: 'dropdown',
				// All items here are already authorized (filtered above)
				items: groupPlugins.map((p): ResolvedMenuItem => {
					const pluginPath = p.primary_route ?? `/${p.id}`;
					return {
						id: p.id,
						label: p.title ?? p.name ?? p.id,
						path: pluginPath,
						icon: p.icon ?? defaults.icon,
						description: p.description,
						accessible: true,
						isCurrent: currentPath === pluginPath || currentPath.startsWith(pluginPath + '/'),
					};
				}),
			};
		})
		.filter(g => g.items && g.items.length > 0)
		.sort((a, b) => a.group.localeCompare(b.group));
}

/**
 * Build group for orphaned plugins (not in menu_structure)
 * Uses pre-computed auth results to filter inaccessible plugins
 */
function buildOrphanGroup(
	plugins: PluginManifest[],
	authResults: Map<string, boolean | 'unknown'>,
	defaults: MenuDefaults,
	currentPath: string
): ResolvedMenuGroup {
	const items: ResolvedMenuItem[] = [];
	
	for (const plugin of plugins) {
		// SECURITY: Fail-closed - skip unless explicitly authorized
		const accessible = authResults.get(plugin.id);
		if (accessible !== true) continue;
		
		const path = plugin.primary_route ?? `/${plugin.id}`;
		items.push({
			id: plugin.id,
			label: plugin.title ?? plugin.name ?? plugin.id,
			path: path,
			icon: plugin.icon ?? defaults.icon,
			description: plugin.description ?? defaults.description,
			accessible: true,
			isCurrent: currentPath === path || currentPath.startsWith(path + '/'),
		});
	}
	
	return {
		group: defaults.group,
		icon: 'package',
		type: 'dropdown',
		items,
	};
}

/**
 * Resolves menu structure with full error handling.
 * NEVER throws - always returns a valid menu (possibly empty).
 * 
 * Per Blue Steel: Resilient resolver that gracefully handles:
 * - Missing plugins
 * - Empty groups/columns
 * - Auth check failures
 * - Orphaned plugins
 * 
 * SECURITY: Auth checks are ENABLED by default. Menu only shows plugins
 * the user is authorized to access.
 */
export async function resolveMenuStructure(
	menuConfig: MenuConfig | null,
	plugins: PluginManifest[],
	options: {
		currentPath?: string;
		skipAuthCheck?: boolean;
		signal?: AbortSignal;
	} = {}
): Promise<ResolvedMenuGroup[]> {
	// SECURITY: Auth checks enabled by default (skipAuthCheck = false)
	const { currentPath = '', skipAuthCheck = false } = options;
	
	const pluginMap = new Map(plugins.map(p => [p.id, p]));
	const defaults: MenuDefaults = {
		...FALLBACK_DEFAULTS,
		...(menuConfig?.defaults || {}),
	};
	
	// ==========================================================================
	// CAPABILITY PROJECTION - one capability-document fetch for ALL plugins
	// This is efficient: 1 HTTP call (globally de-duped/cached) regardless of
	// plugin count, not one PDP probe per plugin.
	// ==========================================================================
	const authResults = new Map<string, boolean | 'unknown'>();
	
	if (!skipAuthCheck) {
		const enabledPlugins = plugins.filter(p => p.enabled !== false);
		const projection = await fetchCapabilityProjection(enabledPlugins);
		const visible = new Set(projection.visible_plugins);
		enabledPlugins.forEach(p => authResults.set(p.id, visible.has(p.id)));
	} else {
		// If skipping auth, mark all as accessible
		plugins.forEach(p => authResults.set(p.id, true));
	}
	
	// Track which plugins are referenced in menu_structure
	const referencedPlugins = new Set<string>();
	
	// If no menu_structure, fall back to grouping by plugin.group field
	if (!menuConfig?.menu_structure || menuConfig.menu_structure.length === 0) {
		console.warn('[MegaMenu] No menu_structure in config, using legacy grouping');
		return buildLegacyGrouping(plugins, authResults, defaults, currentPath);
	}
	
	const resolvedGroups: ResolvedMenuGroup[] = [];
	
	for (const groupConfig of menuConfig.menu_structure) {
		try {
			const resolvedGroup = resolveGroup(
				groupConfig, 
				pluginMap, 
				authResults,
				defaults, 
				currentPath
			);
			
			if (!resolvedGroup) continue;
			
			// Track referenced plugins
			if (resolvedGroup.columns) {
				resolvedGroup.columns.forEach(col => 
					col.items.forEach(item => referencedPlugins.add(item.id))
				);
			}
			if (resolvedGroup.items) {
				resolvedGroup.items.forEach(item => referencedPlugins.add(item.id));
			}
			
			resolvedGroups.push(resolvedGroup);
		} catch (error) {
			console.error(`[MegaMenu] Failed to resolve group "${groupConfig.group}":`, error);
			// Continue with next group - don't crash entire menu
		}
	}
	
	// Add orphaned plugins to fallback group (also filtered by auth)
	const orphanedPlugins = plugins.filter(p => 
		p.enabled !== false && 
		!referencedPlugins.has(p.id) &&
		authResults.get(p.id) === true // Only include accessible plugins (fail-closed)
	);
	
	if (orphanedPlugins.length > 0) {
		console.warn(`[MegaMenu] ${orphanedPlugins.length} plugins not in menu_structure, adding to "${defaults.group}"`);
		const otherGroup = buildOrphanGroup(orphanedPlugins, authResults, defaults, currentPath);
		if (otherGroup.items && otherGroup.items.length > 0) {
			resolvedGroups.push(otherGroup);
		}
	}
	
	return resolvedGroups;
}

/**
 * Filter resolved menu structure by search query
 * Returns groups/columns/items that match the search
 */
export function filterMenuBySearch(
	groups: ResolvedMenuGroup[],
	searchQuery: string
): ResolvedMenuGroup[] {
	if (!searchQuery.trim()) {
		return groups;
	}
	
	const query = searchQuery.toLowerCase();
	
	return groups
		.map((group): ResolvedMenuGroup | null => {
			if (group.type === 'mega' && group.columns) {
				const filteredColumns = group.columns
					.map((col): ResolvedMenuColumn | null => {
						const filteredItems = col.items.filter(item =>
							item.label.toLowerCase().includes(query) ||
							item.description?.toLowerCase().includes(query) ||
							item.id.toLowerCase().includes(query)
						);
						return filteredItems.length > 0 ? { ...col, items: filteredItems } : null;
					})
					.filter((col): col is ResolvedMenuColumn => col !== null);
				
				return filteredColumns.length > 0 
					? { ...group, columns: filteredColumns } 
					: null;
			}
			
			if (group.type === 'dropdown' && group.items) {
				const filteredItems = group.items.filter(item =>
					item.label.toLowerCase().includes(query) ||
					item.description?.toLowerCase().includes(query) ||
					item.id.toLowerCase().includes(query)
				);
				
				return filteredItems.length > 0 
					? { ...group, items: filteredItems } 
					: null;
			}
			
			return null;
		})
		.filter((group): group is ResolvedMenuGroup => group !== null);
}

/**
 * Development-only validation warnings
 */
export function validateMenuConfig(
	menuConfig: MenuConfig | null,
	plugins: PluginManifest[]
): string[] {
	// Vite uses import.meta.env.DEV instead of process.env.NODE_ENV
	if (!import.meta.env.DEV) {
		return [];
	}
	
	if (!menuConfig?.menu_structure) {
		return ['No menu_structure defined, using legacy grouping'];
	}
	
	const pluginIds = new Set(plugins.map(p => p.id));
	const warnings: string[] = [];
	const referencedIds = new Set<string>();
	
	// Check for referenced plugins that don't exist
	for (const group of menuConfig.menu_structure) {
		const allItems = [
			...(group.items ?? []),
			...(group.columns?.flatMap(c => c.items) ?? []),
		];
		
		for (const pluginId of allItems) {
			referencedIds.add(pluginId);
			if (!pluginIds.has(pluginId)) {
				warnings.push(`Plugin "${pluginId}" in group "${group.group}" does not exist`);
			}
		}
	}
	
	// Check for plugins not in any menu
	for (const plugin of plugins) {
		if (plugin.enabled !== false && !referencedIds.has(plugin.id)) {
			warnings.push(`Plugin "${plugin.id}" is not in menu_structure (will appear in "Other")`);
		}
	}
	
	if (warnings.length > 0) {
		console.warn('[MegaMenu Config Validation]', warnings);
	}
	
	return warnings;
}

/**
 * Hook to use resolved menu structure with caching
 */
export function useResolvedMenuStructure(
	plugins: PluginManifest[],
	menuConfig: MenuConfig | null,
	options: {
		skipAuthCheck?: boolean;
	} = {}
): {
	groups: ResolvedMenuGroup[];
	loading: boolean;
	error?: string;
} {
	const [groups, setGroups] = React.useState<ResolvedMenuGroup[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | undefined>();
	
	React.useEffect(() => {
		let mounted = true;
		const controller = new AbortController();
		
		async function resolve() {
			setLoading(true);
			setError(undefined);
			
			try {
				const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
				// SECURITY: Auth checks enabled by default (don't override with true)
				const resolved = await resolveMenuStructure(menuConfig, plugins, {
					currentPath,
					skipAuthCheck: options.skipAuthCheck, // Let resolveMenuStructure use its default (false)
					signal: controller.signal,
				});
				
				if (mounted) {
					setGroups(resolved);
					
					// Run validation in development (Vite uses import.meta.env.DEV)
					if (import.meta.env.DEV) {
						validateMenuConfig(menuConfig, plugins);
					}
				}
		} catch (e: any) {
			if (mounted && e?.name !== 'AbortError') {
				setError(e?.message || 'Failed to resolve menu');
				// SECURITY: Fail-closed on error - show nothing rather than expose unauthorized items
				// Pass empty auth results so no items are shown
				setGroups(buildLegacyGrouping(plugins, new Map(), FALLBACK_DEFAULTS, ''));
			}
		} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		}
		
		if (plugins.length > 0) {
			resolve();
		} else {
			setLoading(false);
		}
		
		return () => {
			mounted = false;
			controller.abort();
		};
	}, [plugins, menuConfig, options.skipAuthCheck]);
	
	return { groups, loading, error };
}

export function usePermittedPluginRoutes(refreshMs = 0): { routes: PermittedRoute[]; loading: boolean; error?: string; manifests: PluginManifest[] } {
	const [routes, setRoutes] = React.useState<PermittedRoute[]>([]);
	const [manifests, setManifests] = React.useState<PluginManifest[]>([]);
	const [loading, setLoading] = React.useState<boolean>(true);
	const [error, setError] = React.useState<string | undefined>(undefined);
	const hasLoadedOnceRef = React.useRef(false);
	const inFlightRef = React.useRef(false);
	const lastRefreshAtRef = React.useRef(0);

	React.useEffect(() => {
		let mounted = true;
		const controller = new AbortController();
		const focusRefreshMinAgeMs = 10 * 60 * 1000;

		async function load() {
			if (inFlightRef.current) {
				return;
			}
			inFlightRef.current = true;
			if (!hasLoadedOnceRef.current) {
				setLoading(true);
			}
			setError(undefined);
			try {
				const manifests = await fetchAuthorizedPluginManifests(controller.signal);

				const index: Array<{ pluginId: string; path: string }> = [];
				
				manifests.forEach(m => {
					if (m.manifest) {
						const primaryRoute = (m as Record<string, any>)['primary_route'];
						let inferredPath: string;
						
						if (typeof primaryRoute === 'string' && primaryRoute.startsWith('/') && primaryRoute.length > 1) {
							inferredPath = primaryRoute;
						} else {
							inferredPath = `/${m.id}`;
						}
						
						index.push({ pluginId: m.id, path: inferredPath });
					}
				});

				const permitted: PermittedRoute[] = [];
				
				for (const entry of index) {
					const m = manifests.find(manifest => manifest.id === entry.pluginId);
					if (!m) continue;
					
					const label = humanize(m.id, entry.path, m.title, m.icon);
					if (label === null) continue;
					
					permitted.push({ 
						pluginId: m.id, 
						path: entry.path, 
						label, 
						group: m.group, 
						icon: m.icon,
						description: m.description,
					});
				}

				const unique = dedupe(permitted);
				if (import.meta.env.DEV) {
					console.log('[dynamicNav] Final permitted routes after dedup:', unique.length);
				}
				if (mounted) {
					setRoutes(unique);
					setManifests(manifests);
				}
				lastRefreshAtRef.current = Date.now();
			} catch (e: any) {
				if (mounted) setError(e?.message || 'error');
			} finally {
				inFlightRef.current = false;
				hasLoadedOnceRef.current = true;
				if (mounted) setLoading(false);
			}
		}

		load();
		const maybeRefreshFromFocus = () => {
			if (document.visibilityState !== 'visible') return;
			const ageMs = Date.now() - lastRefreshAtRef.current;
			if (ageMs < focusRefreshMinAgeMs) return;
			void load();
		};
		window.addEventListener('focus', maybeRefreshFromFocus);
		document.addEventListener('visibilitychange', maybeRefreshFromFocus);
		let id: number | undefined;
		if (refreshMs > 0) { id = window.setInterval(load, refreshMs); }
		return () => {
			mounted = false;
			inFlightRef.current = false;
			hasLoadedOnceRef.current = false;
			controller.abort();
			window.removeEventListener('focus', maybeRefreshFromFocus);
			document.removeEventListener('visibilitychange', maybeRefreshFromFocus);
			if (id) window.clearInterval(id);
		};
	}, [refreshMs]);

	return { routes, loading, error, manifests };
}

// Minimal dropdown menu component (optional usage in layout)
// Blue Steel: All colors via CSS variables, zero hardcoded values
export function PluginsMenu({ groupLabel = 'Apps' }: { groupLabel?: string }) {
  const { routes, loading } = usePermittedPluginRoutes();
  if (loading && routes.length === 0) return null;
  if (!routes.length) return null;
  
  // Blue Steel: Inline styles use CSS variables for theme compliance
  const btnStyle: React.CSSProperties = {
    background: 'var(--header-nav-hover-bg, color-mix(in srgb, var(--header-text) 6%, transparent))',
    color: 'var(--header-text, var(--main-text))',
    border: '1px solid var(--header-nav-hover-border, color-mix(in srgb, var(--header-text) 10%, transparent))',
    borderRadius: 'var(--radius-md, 8px)',
    padding: '6px 10px',
    fontSize: 'var(--font-size-sm, 14px)',
    height: 34,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer'
  };
  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    minWidth: 180,
    background: 'var(--dropdown-bg, var(--card-bg))',
    border: '1px solid var(--dropdown-border, var(--border-color))',
    borderRadius: 'var(--radius-md, 8px)',
    boxShadow: 'var(--dropdown-shadow, 0 8px 24px var(--shadow-color))',
    padding: '6px 0',
    zIndex: 50
  };
  const itemStyle: React.CSSProperties = {
    padding: '8px 12px',
    color: 'var(--dropdown-text, var(--main-text))',
    textDecoration: 'none',
    display: 'block'
  };
  return (
    <div className="apps-menu" style={{ position: 'relative', marginLeft: 12, alignSelf: 'center' }}>
      <details style={{ position: 'relative' }}>
        <summary style={btnStyle}><span>{groupLabel}</span><span style={{ opacity: 0.8 }}>▾</span></summary>
        <div style={menuStyle} role="menu">
          {routes.map(r => (
            <a key={r.path} href={r.path} style={itemStyle} role="menuitem">{r.label}</a>
          ))}
        </div>
      </details>
    </div>
  );
}

// =============================================================================
// HEADER WIDGET SLOT
// Discovers plugins that contribute header widgets, lazy-loads their bundles,
// and returns the resolved React components for rendering in the shell header.
// =============================================================================

export interface HeaderWidget {
	pluginId: string;
	component: React.ComponentType<any>;
	title?: string;
}

export function useHeaderWidgets(): { widgets: HeaderWidget[]; loading: boolean } {
	const [widgets, setWidgets] = React.useState<HeaderWidget[]>([]);
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		let mounted = true;

		async function resolveWidgets() {
			try {
				const manifests = await fetchAuthorizedPluginManifests();
				// Find plugins that declare a header widget contribution
				const headerPlugins = manifests.filter(m =>
					m.contributions?.widgets?.some(w => w.slot === 'header')
				);

				if (headerPlugins.length === 0) {
					if (mounted) { setWidgets([]); setLoading(false); }
					return;
				}

				// Lazy-load each plugin that contributes a header widget
				const { loadPlugin } = await import('./registry');
				const resolved: HeaderWidget[] = [];

				for (const manifest of headerPlugins) {
					try {
						const plugin: LoadedPlugin | null = await loadPlugin(manifest.id);
						if (!plugin) continue;

						// Match each header widget contribution to an exported component
						const widgetDefs = manifest.contributions?.widgets?.filter(w => w.slot === 'header') || [];
						for (const def of widgetDefs) {
							const comp = plugin.widgets?.[def.component] || (plugin as any).routes?.[def.component];
							if (comp && typeof comp === 'function') {
								resolved.push({
									pluginId: manifest.id,
									component: comp,
									title: def.title || undefined,
								});
							} else {
								console.warn(`[HeaderWidgets] ${manifest.id}: widget component "${def.component}" not found in exports`);
							}
						}
					} catch (err) {
						console.error(`[HeaderWidgets] Failed to load widget from ${manifest.id}:`, err);
					}
				}

				if (mounted) setWidgets(resolved);
			} catch (err) {
				console.error('[HeaderWidgets] Failed to resolve header widgets:', err);
			} finally {
				if (mounted) setLoading(false);
			}
		}

		resolveWidgets();
		return () => { mounted = false; };
	}, []);

	return { widgets, loading };
}
